import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Minus, Plus, Users } from 'lucide-react';
import type { Order, RestaurantTable } from '@sebastian/shared';
import { Badge, Button, Field, Input, Modal } from './ui';

/**
 * Re-seating an order that is already open: how many are sitting there, and
 * which table they are sitting at. Both are wrong often enough on a real floor
 * — a party grows, or is moved to a bigger table once one frees up — and until
 * now the only way to correct either was to void the bill and start again.
 *
 * This moves the whole order. Moving part of one is MoveItemDialog's job: that
 * splits a bill in two, this one does not touch the bill at all.
 */
export function SeatingDialog({
  order,
  tables,
  open,
  busy,
  onCancel,
  onConfirm,
}: {
  order: Order;
  tables: RestaurantTable[];
  open: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (change: { tableId: string; guestCount: number }) => void;
}) {
  const [guests, setGuests] = useState(order.guestCount);
  // Typed as well as stepped, so the box keeps its own string and a
  // half-finished number does not snap back under the waiter's fingers.
  const [guestsText, setGuestsText] = useState(String(order.guestCount));
  const [tableId, setTableId] = useState(order.tableId);

  useEffect(() => {
    if (!open) return;
    setGuests(order.guestCount);
    setGuestsText(String(order.guestCount));
    setTableId(order.tableId);
  }, [open, order.guestCount, order.tableId]);

  /* The current table always belongs in the list, whatever its status — it is
     the one the party is on. Every other table has to be genuinely free. */
  const choices = useMemo(
    () =>
      tables.filter(
        (table) => table.id === order.tableId || (table.isActive && table.activeOrder === null),
      ),
    [tables, order.tableId],
  );

  const commitGuests = (next: number) => {
    const clamped = Math.min(100, Math.max(1, Math.round(next) || 1));
    setGuests(clamped);
    setGuestsText(String(clamped));
  };

  const target = choices.find((table) => table.id === tableId) ?? null;
  const movingTable = tableId !== order.tableId;
  const changed = movingTable || guests !== order.guestCount;

  return (
    <Modal
      open={open}
      onClose={busy ? () => undefined : onCancel}
      title="Table & guests"
      subtitle={`Order #${order.orderNumber} · currently Table ${order.tableNumber}`}
      size="lg"
      footer={
        <>
          <Button variant="ghost" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="lg"
            loading={busy}
            disabled={busy || !changed}
            onClick={() => onConfirm({ tableId, guestCount: guests })}
          >
            {busy ? 'Saving…' : changed ? 'Save changes' : 'Nothing changed'}
          </Button>
        </>
      }
    >
      <div className="space-y-7">
        <Field
          label="Guests"
          hint={
            target && guests > target.capacity
              ? `Above Table ${target.number}'s stated capacity of ${target.capacity}.`
              : 'How many are sitting at the table.'
          }
        >
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              className="h-12 w-12 p-0"
              aria-label="One fewer guest"
              disabled={busy || guests <= 1}
              onClick={() => commitGuests(guests - 1)}
            >
              <Minus size={18} />
            </Button>
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              max={100}
              step={1}
              aria-label="Number of guests"
              disabled={busy}
              value={guestsText}
              onChange={(event) => {
                const raw = event.target.value;
                setGuestsText(raw);
                const parsed = Number.parseInt(raw, 10);
                if (Number.isFinite(parsed)) setGuests(Math.min(100, Math.max(1, parsed)));
              }}
              onFocus={(event) => event.target.select()}
              onBlur={() => commitGuests(guests)}
              className="tnum w-20 text-center font-display text-2xl"
            />
            <Button
              variant="secondary"
              className="h-12 w-12 p-0"
              aria-label="One more guest"
              disabled={busy || guests >= 100}
              onClick={() => commitGuests(guests + 1)}
            >
              <Plus size={18} />
            </Button>

            <div className="ml-2 flex flex-wrap gap-1.5">
              {[2, 4, 6, 8].map((count) => (
                <button
                  key={count}
                  type="button"
                  disabled={busy}
                  onClick={() => commitGuests(count)}
                  className={clsx(
                    'h-11 min-w-11 rounded-lg border px-3 text-sm font-semibold transition',
                    guests === count
                      ? 'border-accent bg-accent/15 text-accent-strong'
                      : 'border-line text-ink-soft hover:border-line-strong hover:text-ink',
                  )}
                >
                  {count}
                </button>
              ))}
            </div>
          </div>
        </Field>

        <div>
          <div className="mb-2 flex items-baseline justify-between gap-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-ink-faint">
              Table
            </span>
            <span className="text-[11px] text-ink-faint">
              Only free tables can be moved onto.
            </span>
          </div>

          {choices.length <= 1 ? (
            <p className="rounded-lg border border-dashed border-line px-4 py-6 text-center text-sm text-ink-faint">
              Every other table is occupied, so there is nowhere to move this party.
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
              {choices.map((table) => {
                const current = table.id === order.tableId;
                const picked = table.id === tableId;
                return (
                  <button
                    key={table.id}
                    type="button"
                    disabled={busy}
                    aria-pressed={picked}
                    onClick={() => setTableId(table.id)}
                    className={clsx(
                      'flex flex-col items-center gap-1 rounded-lg border px-2 py-3 text-center transition disabled:cursor-not-allowed disabled:opacity-60',
                      picked
                        ? 'border-accent bg-accent/12'
                        : 'border-line hover:border-line-strong',
                    )}
                  >
                    <span
                      className={clsx(
                        'font-display text-xl',
                        picked ? 'text-accent-strong' : 'text-ink',
                      )}
                    >
                      {String(table.number).padStart(2, '0')}
                    </span>
                    <span
                      className={clsx(
                        'text-[10px] uppercase tracking-wider',
                        !current && table.status === 'RESERVED' ? 'text-caution' : 'text-ink-faint',
                      )}
                    >
                      {current
                        ? 'Here now'
                        : table.status === 'RESERVED'
                          ? 'Reserved'
                          : `Seats ${table.capacity}`}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {movingTable && target ? (
            <p className="mt-3 flex items-center gap-2 text-sm text-ink-soft">
              <Users size={15} />
              Moving the whole bill to Table {target.number}
              {target.section ? ` · ${target.section}` : ''}.
              <Badge tone="caution">Table {order.tableNumber} frees up</Badge>
            </p>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
